/* eslint-disable react/no-danger */

import { SubTitleStyled, LeftSide } from '@/app/components/search/components/Title/Subtitle/styled.ts';
import React from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useSearchDefaultData } from '@/app/components/search/searchDefaultDataProvider.tsx';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { Country, SearchModes, type Transport } from '@/domainLogic/index.ts';
import dynamic from 'next/dynamic';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';

export type SubtitleProps = {
  isWaiting: boolean;
  loadingTitle: string | null;
  subTitle: string | null;
  showCustomCheckinCheckout: boolean;
  isIndex: boolean | undefined;
  transport: Transport | undefined;
  hideSubTitleText: boolean;
};

const CustomCheckinCheckout = dynamic(() => import('@/app/components/search/components/Title/CustomCheckinCheckout'), {
  ssr: false,
});
const AtolProtected = dynamic(() => import('@/app/components/search/components/Title/AtolProtected'), { ssr: false });

const Subtitle = ({
  isWaiting,
  loadingTitle,
  subTitle,
  transport,
  showCustomCheckinCheckout,
  isIndex,
  hideSubTitleText,
}: SubtitleProps) => {
  const isClient = useIsClient();
  const { searchInfoData, localization } = usePageWrapperData();
  const { isMobile } = useCommonClient();
  const { productsData } = useSearchPageData();
  const { defaultValues } = useSearchDefaultData();
  const { t } = useTranslation();
  const { searchMode } = searchInfoData;
  const isDP = searchMode === SearchModes.DP;
  const isTrain = searchMode === SearchModes.TH;
  const showAtolProtected = (isDP || isTrain) && localization.country === Country.GB && !isIndex;
  const subtitleLabel = isWaiting ? loadingTitle : subTitle;
  const showRankingBox = !!localization.rankingLink && !!t('vacation.oss.indexcollapsed.ranking');
  const loadingLabel = t('vacation.oss.title.searching');
  const { data: transportData } = transport || {};
  const { accData } = productsData;
  const { defaultDates: searchDates } = defaultValues;

  const layout = () => {
    if (isWaiting) {
      return <span role="status" aria-live="polite" dangerouslySetInnerHTML={{ __html: loadingLabel }} />;
    }

    if (showCustomCheckinCheckout && subtitleLabel) {
      return (
        <div>
          <span role="status" aria-live="polite" dangerouslySetInnerHTML={{ __html: subtitleLabel }} />
          <CustomCheckinCheckout searchDates={searchDates} accData={accData} transportData={transportData} />
        </div>
      );
    }

    if (subtitleLabel) {
      return <span role="status" aria-live="polite" dangerouslySetInnerHTML={{ __html: subtitleLabel }} />;
    }

    return null;
  };

  return (
    <SubTitleStyled
      addBottomMargin={showAtolProtected && (!showRankingBox || isWaiting) && isMobile}
      isWaiting={isWaiting}
      showAtolProtected={showAtolProtected}
    >
      {!hideSubTitleText && (
        <LeftSide>
          {layout()}
          {isClient && showAtolProtected && <AtolProtected />}
        </LeftSide>
      )}
    </SubTitleStyled>
  );
};

export default Subtitle;
